// apps/web/src/lib/badges/badgeSignals.ts
// ─────────────────────────────────────────────────────────────────────────
// FORWARD-ONLY badge signals (durable, one-way flags).
//
// Some badges depend on a NEW behavior the app can't prove from history —
// a fresh simulator run, an Impact Lab run, a freedom projection, an Apply
// action. When that action happens live, the surface calls markSignal(); we
// persist the flag so the badge stays earned across reloads.
//
// These are honest one-way booleans. Once set they never clear (matching the
// "permanent proof" model). They are NEVER set by the boot sweep.
// ─────────────────────────────────────────────────────────────────────────

import type { ForwardSignals } from '@nisk/core/badges';

const SIGNALS_KEY = 'nis.badges.signals';
const SIGNALS_EVENT = 'nis:badge-signals';

export type BadgeSignal = keyof ForwardSignals;

function read(): ForwardSignals {
	try {
		const raw = localStorage.getItem(SIGNALS_KEY);
		if (raw) return JSON.parse(raw) as ForwardSignals;
	} catch {
		/* noop */
	}
	return {};
}

function write(s: ForwardSignals): void {
	try {
		localStorage.setItem(SIGNALS_KEY, JSON.stringify(s));
		window.dispatchEvent(new Event(SIGNALS_EVENT));
	} catch {
		/* noop */
	}
}

export function readForwardSignals(): ForwardSignals {
	return read();
}

/**
 * Set a forward signal to true (idempotent). Returns true if this call
 * actually flipped the flag (i.e. it was the first time), so callers can
 * decide whether to trigger an evaluation/toast.
 */
export function markSignal(signal: BadgeSignal): boolean {
	const s = read();
	if (s[signal]) return false;
	s[signal] = true;
	write(s);
	return true;
}

